import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { CopyX, Loader2, Merge, Sparkles, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DuplicatesDialog = ({ open, onOpenChange, pairs, canManage, onMerged }) => {
  const [previewIdx, setPreviewIdx] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingIdx, setLoadingIdx] = useState(null);
  const [applying, setApplying] = useState(false);

  const startMerge = async (p, i) => {
    setLoadingIdx(i);
    setPreview(null);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/boilerplate/merge-preview`,
        { a_id: p.a.id, b_id: p.b.id }, { withCredentials: true });
      setPreview(res.data);
      setPreviewIdx(i);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build the merge preview');
    } finally {
      setLoadingIdx(null);
    }
  };

  const applyMerge = async () => {
    setApplying(true);
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/merge`, {
        keep_id: preview.keep_id, remove_id: preview.remove_id,
        title: preview.title, body: preview.body,
      }, { withCredentials: true });
      toast.success(`Merged into "${preview.title}" — keywords are regenerating`);
      setPreview(null);
      setPreviewIdx(null);
      onOpenChange(false);
      onMerged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPreview(null); setPreviewIdx(null); } onOpenChange(v); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rfp-duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CopyX className="w-4 h-4 text-amber-600" />Possible Duplicates
          </DialogTitle>
          <DialogDescription>
            Capability pairs whose wording overlaps heavily. Merge combines both into the more-used block with AI-blended wording — you review before anything is written.
          </DialogDescription>
        </DialogHeader>
        {(!pairs || pairs.length === 0) ? (
          <p className="text-sm text-slate-500 py-4 text-center" data-testid="rfp-duplicates-empty">No overlapping capabilities found — the library is clean.</p>
        ) : (
          <div className="space-y-2 max-h-[65vh] overflow-y-auto" data-testid="rfp-duplicates-list">
            {pairs.map((p, i) => (
              <div key={i} className="border border-amber-200 bg-amber-50/50 rounded-md px-3 py-2" data-testid={`rfp-duplicate-pair-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[10px] font-bold text-amber-700">{Math.round(p.similarity * 100)}% overlap</span>
                  {canManage && previewIdx !== i && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                      onClick={() => startMerge(p, i)} disabled={loadingIdx !== null}
                      data-testid={`rfp-duplicate-merge-${i}`}>
                      {loadingIdx === i ? <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" /> : <Merge className="w-2.5 h-2.5 mr-1" />}
                      Merge with AI
                    </Button>
                  )}
                </div>
                <p className="text-xs text-[#0F2040]">
                  <span className="font-mono text-[9px] text-slate-400 mr-1">{p.a.capability_id}</span>
                  <span className="font-medium">{p.a.title}</span>
                </p>
                <p className="text-xs text-[#0F2040]">
                  <span className="font-mono text-[9px] text-slate-400 mr-1">{p.b.capability_id}</span>
                  <span className="font-medium">{p.b.title}</span>
                </p>
                {previewIdx === i && preview && (
                  <div className="mt-2 border border-[#4ABFCF]/40 bg-white rounded-md p-2.5 space-y-2" data-testid="rfp-merge-preview">
                    <p className="text-[10px] text-slate-600 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-[#4ABFCF]" />
                      AI-combined wording — <span className="font-semibold">{preview.keep_capability_id}</span> survives,
                      <span className="font-semibold ml-1">{preview.remove_capability_id} "{preview.remove_title}"</span> will be deleted.
                    </p>
                    <Input value={preview.title} onChange={e => setPreview(pr => ({ ...pr, title: e.target.value }))}
                      className="h-8 text-xs" data-testid="rfp-merge-title-input" />
                    <Textarea value={preview.body} onChange={e => setPreview(pr => ({ ...pr, body: e.target.value }))}
                      rows={8} className="text-xs" data-testid="rfp-merge-body-input" />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => { setPreview(null); setPreviewIdx(null); }} data-testid="rfp-merge-cancel">
                        <X className="w-3 h-3 mr-1" />Cancel
                      </Button>
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={applyMerge} disabled={applying} data-testid="rfp-merge-confirm">
                        {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        Confirm merge
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
